// @ts-nocheck

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Users } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { EventFormData, PricingTier, AgeGroup } from "../CreateEventForm";







interface RegistrationPricingTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
  eventId?: string; // For dynamic age groups in edit mode
}

interface DiscountPolicy {
  id: string;
  name: string;
}

interface EventRegistrationForm {
  id: string;
  name: string;
}

interface PacketTemplate {
  id: string;
  name: string;
}

interface EventAgeGroup {
  id: string;
  name: string;
  is_active: boolean;
}

// Common age groups for packet configuration (fallback)
const COMMON_AGE_GROUPS = [
  '8U', '9U', '10U', '11U', '12U', '13U', '14U', '15U', '16U', '17U', '18U',
  'Elementary', 'Middle School', 'High School', 'JV', 'Varsity', 'Adult'
];

const RegistrationPricingTab: React.FC<RegistrationPricingTabProps> = ({ 
  formData, 
  updateFormData,
  eventId
}) => {
  const [discountPolicies, setDiscountPolicies] = useState<DiscountPolicy[]>([]);
  const [eventRegistrationForms, setEventRegistrationForms] = useState<EventRegistrationForm[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [packetTemplates, setPacketTemplates] = useState<PacketTemplate[]>([]);
  const [packetAgeGroups, setPacketAgeGroups] = useState<EventAgeGroup[]>([]);

  useEffect(() => {
    fetchDiscountPolicies();
    fetchEventRegistrationForms();
    fetchPacketTemplates();
    if (eventId) {
      fetchEventAgeGroups();
    }
  }, [eventId]);

  useEffect(() => {
    // Initialize pricing tiers from formData
    if (formData.pricing_tiers && formData.pricing_tiers.length > 0) {
      setPricingTiers(formData.pricing_tiers);
    }
    
    // Initialize age groups from formData - ensure they're properly loaded
    if (formData.age_groups && formData.age_groups.length > 0) {
      console.log('Setting age groups from formData:', formData.age_groups);
      setAgeGroups(formData.age_groups);
    } else {
      console.log('No age groups found in formData');
      setAgeGroups([]);
    }
  }, [formData.pricing_tiers, formData.age_groups]);

  const fetchDiscountPolicies = async () => {
    try {
      const { data, error } = await supabase
        .from('event_discount_policies')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setDiscountPolicies(data || []);
    } catch (error) {
      console.error('Error fetching discount policies:', error);
    }
  };

  const fetchEventRegistrationForms = async () => {
    try {
      const { data, error } = await supabase
        .from('event_registration_forms')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setEventRegistrationForms(data || []);
    } catch (error) {
      console.error('Error fetching event registration forms:', error);
    }
  };

  const fetchPacketTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('packet_templates')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setPacketTemplates(data || []);
    } catch (error) {
      console.error('Error fetching packet templates:', error);
    }
  };

  const fetchEventAgeGroups = async () => {
    if (!eventId) return;
    
    try {
      const { data, error } = await supabase
        .from('event_age_groups')
        .select('id, name, is_active')
        .eq('event_id', eventId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setPacketAgeGroups(data || []);
    } catch (error) {
      console.error('Error fetching event age groups:', error);
      // Fall back to empty array on error
      setPacketAgeGroups([]);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    updateFormData({ [field]: value });
  };

  const addPricingTier = () => {
    const newTier: PricingTier = {
      tier_name: '',
      price: '',
      registration_form_id: '',
      is_active: true
    };
    const updatedTiers = [...pricingTiers, newTier];
    setPricingTiers(updatedTiers);
    updateFormData({ pricing_tiers: updatedTiers });
  };

  const updatePricingTier = (index: number, field: keyof PricingTier, value: any) => {
    const updatedTiers = pricingTiers.map((tier, i) => 
      i === index ? { ...tier, [field]: value } : tier
    );
    setPricingTiers(updatedTiers);
    updateFormData({ pricing_tiers: updatedTiers });
  };

  const deletePricingTier = (index: number) => {
    const updatedTiers = pricingTiers.filter((_, i) => i !== index);
    setPricingTiers(updatedTiers);
    updateFormData({ pricing_tiers: updatedTiers });
  };

  const addAgeGroup = () => {
    const newGroup: AgeGroup = {
      name: '',
      registration_fee: '',
      registration_form_id: '',
      is_active: true
    };
    const updatedGroups = [...ageGroups, newGroup];
    setAgeGroups(updatedGroups);
    updateFormData({ age_groups: updatedGroups });
  };

  const updateAgeGroup = (index: number, field: keyof AgeGroup, value: any) => {
    const updatedGroups = ageGroups.map((group, i) => 
      i === index ? { ...group, [field]: value } : group
    );
      console.log('Updated age groups:', updatedGroups); // ADD THIS LINE
    setAgeGroups(updatedGroups);
    updateFormData({ age_groups: updatedGroups });
  };

  const deleteAgeGroup = (index: number) => {
    const updatedGroups = ageGroups.filter((_, i) => i !== index);
    setAgeGroups(updatedGroups);
    updateFormData({ age_groups: updatedGroups });
  };

  return (
    <div className="space-y-6">
     

      {/* Registration Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Registration Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="accept_online_registrations"
              checked={formData.accept_online_registrations}
              onCheckedChange={(checked) => updateFormData({ accept_online_registrations: checked })}
            />
            <Label htmlFor="accept_online_registrations">Accept Online Registrations</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="allow_checkin"
              checked={formData.allow_checkin}
              onCheckedChange={(checked) => updateFormData({ allow_checkin: checked })}
            />
            <Label htmlFor="allow_checkin">Allow Check-in</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="allow_coupons"
              checked={formData.allow_coupons}
              onCheckedChange={(checked) => updateFormData({ allow_coupons: checked })}
            />
            <Label htmlFor="allow_coupons">Allow Coupons</Label>
          </div>

          <div>
            <Label htmlFor="discount_policy">Assign Discount Policy</Label>
            <Select value={formData.discount_policy_id || ""} onValueChange={(value) => updateFormData({ discount_policy_id: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Select discount policy" />
              </SelectTrigger>
              <SelectContent>
                {discountPolicies.map((policy) => (
                  <SelectItem key={policy.id} value={policy.id}>
                    {policy.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="external_registration_url">External Registration URL</Label>
            <Input
              id="external_registration_url"
              type="url"
              value={formData.external_registration_url}
              onChange={(e) => updateFormData({ external_registration_url: e.target.value })}
              placeholder="https://example.com/register"
            />
            <p className="text-sm text-muted-foreground mt-1">
              If provided, users will be redirected to this URL for registration instead of the built-in form.
            </p>
          </div>

          {!formData.accept_online_registrations && (
            <div>
              <Label htmlFor="pricing_display_text">Pricing Display Text</Label>
              <Input
                id="pricing_display_text"
                value={formData.pricing_display_text}
                onChange={(e) => updateFormData({ pricing_display_text: e.target.value })}
                placeholder="e.g., From $99, Contact for pricing"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Text to display in the pricing box when online registration is disabled.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registration & Pricing Card */}
      <Card>
        <CardHeader>
          <CardTitle>Registration & Pricing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.accept_online_registrations ? (
            <>
              <div>
                <Label htmlFor="registration_type">Registration Type</Label>
                <Select value={formData.registration_type} onValueChange={(value) => handleInputChange('registration_type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select registration type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Roster Submission Deadline - Only for Team Registration */}
              {formData.registration_type === 'team' && (
                <div>
                  <Label htmlFor="roster_deadline">Roster Submission Deadline</Label>
                  <Input
                    id="roster_deadline"
                    type="date"
                    value={formData.roster_deadline || ''}
                    onChange={(e) => updateFormData({ roster_deadline: e.target.value })}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Set the deadline for teams to submit their final rosters.
                  </p>
                </div>
              )}

              {/* Individual Registration - Pricing Tiers */}
              {formData.registration_type === 'individual' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Event Pricing Tiers</Label>
                    <Button type="button" onClick={addPricingTier} variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Pricing Tier
                    </Button>
                  </div>
                  
                  {pricingTiers.map((tier, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor={`tier-name-${index}`}>Tier Name</Label>
                          <Input
                            id={`tier-name-${index}`}
                            value={tier.tier_name}
                            onChange={(e) => updatePricingTier(index, 'tier_name', e.target.value)}
                            placeholder="e.g., Early Bird, Regular, VIP"
                          />
                        </div>
                        <div>
                          <Label htmlFor={`tier-price-${index}`}>Price ($)</Label>
                          <Input
                            id={`tier-price-${index}`}
                            type="text"
                            value={tier.price ?? ''}
                            onChange={(e) => updatePricingTier(index, 'price', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor={`tier-form-${index}`}>Registration Form</Label>
                        <Select 
                          value={tier.registration_form_id} 
                          onValueChange={(value) => updatePricingTier(index, 'registration_form_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select registration form" />
                          </SelectTrigger>
                          <SelectContent>
                            {eventRegistrationForms.map((form) => (
                              <SelectItem key={form.id} value={form.id}>
                                {form.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Switch
                            id={`tier-active-${index}`}
                            checked={tier.is_active}
                            onCheckedChange={(checked) => updatePricingTier(index, 'is_active', checked)}
                          />
                          <Label htmlFor={`tier-active-${index}`}>Active</Label>
                        </div>
                        
                        <Button
                          type="button"
                          onClick={() => deletePricingTier(index)}
                          variant="outline"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Team Registration - Age Groups */}
              {formData.registration_type === 'team' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Event Age Groups ({ageGroups.length} groups)</Label>
                    <Button type="button" onClick={addAgeGroup} variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Age Group
                    </Button>
                  </div>
                  
                  {ageGroups.length === 0 ? (
                    <div className="text-muted-foreground text-sm border rounded-lg p-4">
                      No age groups configured. Click "Add Age Group" to create one.
                    </div>
                  ) : (
                    ageGroups.map((group, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`group-name-${index}`}>Age Group Name</Label>
                            <Input
                              id={`group-name-${index}`}
                              value={group.name || ''}
                              onChange={(e) => updateAgeGroup(index, 'name', e.target.value)}
                              placeholder="e.g., 11U, 12U, Varsity"
                            />
                          </div>
                          <div>
                            <Label htmlFor={`group-price-${index}`}>Price ($)</Label>
                            <Input
                              id={`group-price-${index}`}
                              type="text"
                              value={group.registration_fee ?? ''}
                              onChange={(e) => updateAgeGroup(index, 'registration_fee', e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor={`group-form-${index}`}>Registration Form</Label>
                          <Select 
                            value={group.registration_form_id || ''} 
                            onValueChange={(value) => updateAgeGroup(index, 'registration_form_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select registration form" />
                            </SelectTrigger>
                            <SelectContent>
                              {eventRegistrationForms.map((form) => (
                                <SelectItem key={form.id} value={form.id}>
                                  {form.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
  <div className="flex items-center space-x-2 mt-2">
    <Switch
      id={`group-special-request-${index}`}
      checked={group.accept_special_requests || false}
      onCheckedChange={(checked) => updateAgeGroup(index, 'accept_special_requests', checked)}
    />
    <Label htmlFor={`group-special-request-${index}`}>Accept Special Requests</Label>
  </div>

  {group.accept_special_requests && (
    <div>
      <Label htmlFor={`group-special-fee-${index}`}>Special Request Fee ($)</Label>
      <Input
        id={`group-special-fee-${index}`}
        type="number"
        step="0.01"
        min="0"
        value={group.special_request_fee ?? ''}
        onChange={(e) => updateAgeGroup(index, 'special_request_fee', parseFloat(e.target.value) || 0)}
        placeholder="e.g., 25.00"
      />
    </div>
  )}
</div>

                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`group-active-${index}`}
                              checked={group.is_active !== false}
                              onCheckedChange={(checked) => updateAgeGroup(index, 'is_active', checked)}
                            />
                            <Label htmlFor={`group-active-${index}`}>Active</Label>
                          </div>
                          
                          <Button
                            type="button"
                            onClick={() => deleteAgeGroup(index)}
                            variant="outline"
                            size="sm"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-muted-foreground text-sm border rounded-lg p-4">
              Enable "Accept Online Registrations" to configure pricing and registration options.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Packet Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            📄 Packet Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="packet_enabled"
              checked={formData.packet_enabled || false}
              onCheckedChange={(checked) => updateFormData({ packet_enabled: checked })}
            />
            <Label htmlFor="packet_enabled">Enable Packet Purchase</Label>
          </div>

          {formData.packet_enabled && (
            <>
              <div>
                <Label htmlFor="packet_price">Packet Price ($)</Label>
                <Input
                  id="packet_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.packet_price || 0}
                  onChange={(e) => updateFormData({ packet_price: parseFloat(e.target.value) || 0 })}
                  placeholder="25.00"
                />
              </div>

              <div>
                <Label htmlFor="default_packet_template">Default Packet Template</Label>
                <Select 
                  value={formData.default_packet_template_id || ""} 
                  onValueChange={(value) => updateFormData({ default_packet_template_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select packet template" />
                  </SelectTrigger>
                  <SelectContent>
                    {packetTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="packet_include_all_age_groups"
                    checked={formData.packet_include_all_age_groups ?? true}
                    onCheckedChange={(checked) => updateFormData({ packet_include_all_age_groups: checked })}
                  />
                  <Label htmlFor="packet_include_all_age_groups">Include All Age Groups</Label>
                </div>

                {!formData.packet_include_all_age_groups && (
                  <div>
                    <Label>Select Age Groups for Packet</Label>
                    <div className="grid grid-cols-4 gap-2 mt-2 p-3 border rounded-lg">
                      {(packetAgeGroups.length > 0 ? packetAgeGroups : COMMON_AGE_GROUPS.map(name => ({ id: name, name, is_active: true }))).map((ageGroup) => (
                        <div key={ageGroup.id || ageGroup.name} className="flex items-center space-x-2">
                          <Checkbox
                            id={`age-group-${ageGroup.id || ageGroup.name}`}
                            checked={(formData.packet_default_age_groups || []).includes(ageGroup.id || ageGroup.name)}
                            onCheckedChange={(checked) => {
                              const currentGroups = formData.packet_default_age_groups || [];
                              const groupValue = ageGroup.id || ageGroup.name;
                              const updatedGroups = checked
                                ? [...currentGroups, groupValue]
                                : currentGroups.filter(group => group !== groupValue);
                              updateFormData({ packet_default_age_groups: updatedGroups });
                            }}
                          />
                          <Label htmlFor={`age-group-${ageGroup.id || ageGroup.name}`} className="text-sm">
                            {ageGroup.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationPricingTab;
